import { useState, useEffect, useCallback } from 'react';

export type UserStatus = 'online' | 'away' | 'offline';

interface UseActivityStatusProps {
  websocket?: WebSocket | null;
  isConnected: boolean;
  enabled?: boolean;
}

/**
 * Hook para gerenciar status de atividade baseado em WebSocket + visibilidade da página
 * - online: WebSocket conectado + página visível + usuário ativo
 * - away: WebSocket conectado + página oculta/minimizada  
 * - offline: WebSocket desconectado
 */
export function useActivityStatus({ websocket, isConnected, enabled = true }: UseActivityStatusProps) {
  const [currentStatus, setCurrentStatus] = useState<UserStatus>('offline');

  // Função para enviar mudança de status
  const sendStatusUpdate = useCallback((status: UserStatus) => {
    if (!websocket || !enabled) return;

    try {
      const statusMessage = {
        type: "status_update",
        status,
        timestamp: Date.now()
      };
      
      websocket.send(JSON.stringify(statusMessage));
    } catch (error) {
      console.error('[STATUS] Erro ao enviar status:', error);
    }
  }, [websocket, enabled]);

  // Determinar status baseado em conexão + visibilidade
  const determineStatus = useCallback((): UserStatus => {
    if (!isConnected) {
      return 'offline';
    }
    
    // Se conectado, verificar visibilidade da página
    if (document.hidden || document.visibilityState === 'hidden') {
      return 'away';
    }
    
    return 'online';
  }, [isConnected]);

  // Atualizar status e enviar para servidor
  const updateStatus = useCallback(() => {
    const newStatus = determineStatus();
    
    if (newStatus !== currentStatus) {
      setCurrentStatus(newStatus);
      sendStatusUpdate(newStatus);
    }
  }, [determineStatus, currentStatus, sendStatusUpdate]);

  // Listener para mudanças de visibilidade da página
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      updateStatus();
    };

    const handleFocus = () => {
      updateStatus();
    };

    const handleBlur = () => {
      updateStatus();
    };

    // Event listeners para detectar mudanças de estado
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [updateStatus, enabled]);

  // Atualizar status quando conexão WebSocket muda
  useEffect(() => {
    updateStatus();
  }, [isConnected, updateStatus]);

  // Status inicial quando hook é montado
  useEffect(() => {
    if (enabled) {
      const initialStatus = determineStatus();
      setCurrentStatus(initialStatus);
      sendStatusUpdate(initialStatus);
    }
  }, [enabled]); // Removido dependencies para evitar loops

  return {
    status: currentStatus,
    isOnline: currentStatus === 'online',
    isAway: currentStatus === 'away',
    isOffline: currentStatus === 'offline'
  };
}